import type { ReactNode } from 'react';
import { Link, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { useKadryAccess, type KadryOp } from '@entities/kadry';
import { KadryErrorBanner } from './KadryErrorBanner';

const BackToOverview = styled(Link)`
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
`;

interface Props {
  /** Раздел открыт, если доступна хотя бы одна из операций. */
  anyOf: readonly KadryOp[];
  /** Содержимое; без него — вложенные маршруты (`<Outlet/>`). */
  children?: ReactNode;
}

/**
 * Охрана раздела Кадров: модуль готов и у пользователя есть нужная операция.
 * Иначе — понятное объяснение вместо пустого экрана или 403 от сервера.
 * Сервер всё равно проверяет права сам.
 */
export function KadryGuard({ anyOf, children }: Props) {
  const { info, isLoading, error, canAny } = useKadryAccess();

  if (isLoading) {
    return (
      <Card>
        <SkeletonLine $width="40%" />
        <SkeletonLine />
        <SkeletonLine $width="70%" />
      </Card>
    );
  }
  if (error || !info) {
    return <KadryErrorBanner error={error} title="Модуль недоступен" />;
  }
  if (info.status.state !== 'ready') {
    return (
      <FallbackBanner
        tone={info.status.state === 'unavailable' ? 'danger' : 'info'}
        title={info.status.state === 'unavailable' ? 'Модуль недоступен' : 'Модуль выключен'}
      >
        {info.status.state === 'unavailable'
          ? info.status.reason
          : 'Раздел откроется, когда модуль ОСНОВА.Кадры будет включён.'}
        <br />
        <BackToOverview to="/agents/kadry">← К обзору модуля</BackToOverview>
      </FallbackBanner>
    );
  }
  if (!canAny(anyOf)) {
    return (
      <FallbackBanner tone="info" title="Раздел недоступен">
        У вашей роли в модуле нет доступа к этому разделу.
        <br />
        <BackToOverview to="/agents/kadry">← К обзору модуля</BackToOverview>
      </FallbackBanner>
    );
  }
  return <>{children ?? <Outlet />}</>;
}
